import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
} from "@mui/material";
import { useFormik } from "formik";
import { styled } from "@mui/joy";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import SyncIcon from "@mui/icons-material/Sync";
import { Button } from "@mui/material";
import ReuseableButton from "../../../components/Button/ReusableButton";
import ReuseableSnackbar from "../../../components/Snackbar/Snackbar";
import DocumentsTable from "../../../components/Table/Table";
import ReusableModal from "../../../components/ReusableModal/ReusableModal";
import ReindexDialog from "../../../components/ReindexDialog/ReindexDialog";
import { GetUpload, PostUpload } from "../../../services/apiServices";
import { networkUrls } from "../../../services/networkurls";

const VisuallyHiddenInput = styled("input")`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

const ACCENT = "#4D8FAC";
const ACCENT_DARK = "#3b738a";
const ACCENT_LIGHT = "#eaf4f8";

const FileUpload = ({ defaultTab = 0 }: { defaultTab?: 0 | 1 }) => {
  const isCaseStudy = defaultTab === 0;

  const [isShowSnackbar, setIsShowSnackbar] = useState(false);
  const [triggerGetCall, setTrigegerGetCall] = useState(0);
  const [showMissingModal, setShowMissingModal] = useState(false);
  const [missingCategoryMessage, setMissingCategoryMessage] = useState("");
  const [proceedPayload, setProceedPayload] = useState<FormData | null>(null);
  const [modalType, setModalType] = useState("");
  const [isProceed, setIsProceed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({ color: "", message: "" });
  const [reindexOpen, setReindexOpen] = useState(false);

  const formik: any = useFormik({
    initialValues: { files: [], videoFile: null },
    onSubmit: () => handleSubmit(),
  });

  const handleSuccess = (message: string) => {
    setSnackbarInfo({ color: "success", message });
    setIsShowSnackbar(true);
    formik.resetForm();
    setTrigegerGetCall((prev) => prev + 1);
  };

  const handleFailure = (message: string) => {
    setSnackbarInfo({ color: "error", message: message || "Upload failed" });
    setIsShowSnackbar(true);
  };

  const pollVideoUploadStatus = async (jobId: string): Promise<{ success: boolean; message: string }> => {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const res = await GetUpload(`${networkUrls.videoUploadStatus}/${jobId}`, true);
      const job = res?.data;
      if (job?.status === "completed") {
        return { success: true, message: job.stage || "Video uploaded and indexed successfully." };
      }
      if (job?.status === "failed") {
        return { success: false, message: job.error || job.stage || "Video processing failed." };
      }
      await new Promise((resolve) => setTimeout(resolve, 3000));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    const fd: any = new FormData();

    if (isCaseStudy) {
      if (!formik.values.files?.length) {
        formik.setFieldTouched("files", true);
        setSnackbarInfo({ color: "error", message: "Please select a case study file" });
        setIsShowSnackbar(true);
        setLoading(false);
        return;
      }
      formik.values.files.forEach((file: any) => fd.append("file", file));
      fd.append("upload_type", "case-study");

      const url = networkUrls.uploadCaseStudy;
      try {
        const response = await PostUpload(url, fd, true, true);
        if (response?.status === 200) {
          if (response?.data?.missing_categories?.length) {
            setShowMissingModal(true);
            setMissingCategoryMessage(response?.data?.message);
            setModalType("missing-category");
            setProceedPayload(fd);
          } else {
            handleSuccess(response?.data?.message);
          }
        } else {
          handleFailure(response?.data?.error);
        }
      } catch (error: any) {
        handleFailure(error?.response?.data?.error);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!formik.values.videoFile) {
      formik.setFieldTouched("videoFile", true);
      setSnackbarInfo({ color: "error", message: "Please select a video file" });
      setIsShowSnackbar(true);
      setLoading(false);
      return;
    }
    fd.append("file", formik.values.videoFile);

    try {
      const response = await PostUpload(networkUrls.uploadVideo, fd, true, true);
      const jobId = response?.data?.job_id;
      if (response?.status === 200 && jobId) {
        setSnackbarInfo({ color: "info", message: "Video uploaded. Transcribing and indexing — this can take a few minutes..." });
        setIsShowSnackbar(true);
        const result = await pollVideoUploadStatus(jobId);
        if (result.success) handleSuccess(result.message);
        else handleFailure(result.message);
      } else {
        handleFailure(response?.data?.error);
      }
    } catch (error: any) {
      handleFailure(error?.response?.data?.error);
    } finally {
      setLoading(false);
    }
  };

  const handleProceedUpload = async () => {
    if (!proceedPayload) return;
    setShowMissingModal(false);
    setLoading(true);
    try {
      const response = await PostUpload(networkUrls.uploadCaseStudy + "?force=true", proceedPayload, true, true);
      if (response?.status === 200) handleSuccess(response?.data?.message);
      else handleFailure(response?.data?.error);
    } catch (err: any) {
      handleFailure(err?.response?.data?.error);
    } finally {
      setLoading(false);
      setProceedPayload(null);
    }
  };

  useEffect(() => {
    if (modalType && isProceed) handleProceedUpload();
  }, [modalType, isProceed]);

  const UploadDropArea = ({
    icon, label, hint, accept, fileName, hasError, onFile, onClear,
  }: {
    icon: React.ReactNode;
    label: string;
    hint: string;
    accept: string;
    fileName?: string;
    hasError?: boolean;
    onFile: (file: File | null) => void;
    onClear: () => void;
  }) => (
    <Box>
      <Box
        component="label"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          border: `2px dashed ${hasError ? "#d32f2f" : "#d0d0d0"}`,
          borderRadius: "12px",
          p: 3,
          textAlign: "center",
          backgroundColor: fileName ? ACCENT_LIGHT : "#fafafa",
          cursor: "pointer",
          transition: "border-color 0.2s, background-color 0.2s",
          minHeight: 130,
          "&:hover": { borderColor: ACCENT, backgroundColor: ACCENT_LIGHT },
        }}
      >
        <Box sx={{ color: fileName ? ACCENT : "#aaa", display: "flex" }}>{icon}</Box>
        <Typography variant="body2" fontWeight={600} color={fileName ? ACCENT_DARK : "#555"} sx={{ wordBreak: "break-word", maxWidth: "90%" }}>
          {fileName || label}
        </Typography>
        {!fileName && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
        <VisuallyHiddenInput
          type="file"
          accept={accept}
          onChange={(e) => { onFile(e.target.files?.[0] || null); e.target.value = ""; }}
        />
      </Box>
      {fileName && (
        <Box sx={{ mt: 1 }}>
          <Chip
            icon={<InsertDriveFileIcon sx={{ fontSize: 14 }} />}
            label={fileName}
            onDelete={onClear}
            size="small"
            sx={{
              backgroundColor: ACCENT_LIGHT,
              color: ACCENT_DARK,
              fontWeight: 600,
              maxWidth: "100%",
              "& .MuiChip-icon": { color: ACCENT },
              "& .MuiChip-deleteIcon": { color: ACCENT_DARK },
            }}
          />
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ pr: { xs: 0, md: "20px" } }}>
      {/* Page Header */}
      <Box sx={{ mb: 3, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h6" fontWeight="700" color="#1a1a1a" sx={{ mb: 0.5 }}>
            {isCaseStudy ? "Case Studies" : "Videos"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isCaseStudy
              ? "Upload and manage case studies for the Pre-Sales AI"
              : "Upload and manage video transcripts for the Pre-Sales AI"}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<SyncIcon sx={{ fontSize: 16 }} />}
          onClick={() => setReindexOpen(true)}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.8rem",
            borderColor: ACCENT,
            color: ACCENT,
            height: 34,
            mt: 0.5,
            "&:hover": { backgroundColor: ACCENT_LIGHT, borderColor: ACCENT_DARK },
          }}
        >
          Reindex {isCaseStudy ? "Case Studies" : "Videos"}
        </Button>
      </Box>

      {/* Upload Card */}
      <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: "14px", mb: 2.5, overflow: "hidden" }}>
        <Box sx={{ p: 2, borderBottom: "1px solid #e8e8e8", backgroundColor: "#fafafa" }}>
          <Typography variant="subtitle2" fontWeight={700} color="#444" sx={{ textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.75rem" }}>
            {isCaseStudy ? "Upload Case Study" : "Upload Video & Transcript"}
          </Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <form onSubmit={formik.handleSubmit}>
            {isCaseStudy ? (
              <Box>
                <Box sx={{ maxWidth: 480 }}>
                  <UploadDropArea
                    icon={<CloudUploadIcon sx={{ fontSize: 36 }} />}
                    label="Click to upload or drag & drop"
                    hint="PDF, DOC, DOCX, PPTX — single file"
                    accept=".pdf,.doc,.docx,.pptx"
                    fileName={formik.values.files?.length > 0 ? formik.values.files[0].name : undefined}
                    hasError={formik.touched.files && !formik.values.files?.length}
                    onFile={(file) => formik.setFieldValue("files", file ? [file] : [])}
                    onClear={() => formik.setFieldValue("files", [])}
                  />
                </Box>
                <Box sx={{ mt: 2.5 }}>
                  <ReuseableButton title="Upload" type="submit" loading={loading}
                    sx={{ width: "110px", height: "38px", backgroundColor: ACCENT, borderRadius: "8px", "&:hover": { backgroundColor: ACCENT_DARK } }}
                  />
                </Box>
              </Box>
            ) : (
              <Box>
                <Box sx={{ maxWidth: 480 }}>
                  <UploadDropArea
                    icon={<VideoLibraryIcon sx={{ fontSize: 36 }} />}
                    label="Select Video File"
                    hint="MP4, AVI, MOV and other video formats — transcribed automatically"
                    accept="video/*"
                    fileName={formik.values.videoFile?.name}
                    hasError={formik.touched.videoFile && !formik.values.videoFile}
                    onFile={(file) => { formik.setFieldValue("videoFile", file); formik.setFieldTouched("videoFile", true); }}
                    onClear={() => formik.setFieldValue("videoFile", null)}
                  />
                </Box>
                <Box sx={{ mt: 2.5 }}>
                  <ReuseableButton title="Upload" type="submit" loading={loading}
                    sx={{ width: "110px", height: "38px", backgroundColor: ACCENT, borderRadius: "8px", "&:hover": { backgroundColor: ACCENT_DARK } }}
                  />
                </Box>
              </Box>
            )}
          </form>
        </Box>
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: "14px", p: 2.5, width: "100%" }}>
        <DocumentsTable
          triggerGetCall={triggerGetCall}
          tableName={isCaseStudy ? "Case Studies" : "Videos"}
          type={isCaseStudy ? "documents" : "videos"}
        />
      </Paper>

      {isShowSnackbar && (
        <ReuseableSnackbar
          isShowSnackbar={isShowSnackbar}
          colour={snackbarInfo.color}
          setIsShowSnackbar={setIsShowSnackbar}
          message={snackbarInfo.message}
          autoHideDuration={2000}
        />
      )}

      {showMissingModal && (
        <ReusableModal
          type={modalType}
          open={showMissingModal}
          setOpen={setShowMissingModal}
          category={missingCategoryMessage}
          setIsProceed={setIsProceed}
        />
      )}

      <ReindexDialog
        open={reindexOpen}
        docType={isCaseStudy ? "case-studies" : "videos"}
        label={isCaseStudy ? "Case Studies" : "Videos"}
        onClose={() => setReindexOpen(false)}
        onComplete={() => setTrigegerGetCall((p) => p + 1)}
      />
    </Box>
  );
};

export default FileUpload;
